import { useEffect, useMemo, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { listBundledScenarios } from "@radar-sim/scenarios";
import {
  LocalSimSource,
  type RadarStateSource,
} from "@radar-sim/engine";
import {
  asAreaDataLookup,
  getAreaData,
  getTidalAnomalies,
  makeIsOnLand,
} from "@radar-sim/area-data";
import type { LatLon, Scenario } from "@radar-sim/schema";
import { Button, Heading, Stack } from "./ui/primitives.js";
import { GarminGPSMAP } from "./chrome/GarminGPSMAP.js";
import { RaymarineAxiom } from "./chrome/RaymarineAxiom.js";
import type { ChromeComponent } from "./chrome/types.js";
import { PPI } from "./ppi/PPI.js";
import { DEFAULT_DISPLAY_STATE, type DisplayState } from "./ppi/displayState.js";
import { ScenarioAuthoring } from "./authoring/ScenarioAuthoring.js";
import { listSavedScenarios } from "./authoring/library.js";
import { Scenario as ScenarioSchema } from "@radar-sim/schema";
import { PlottingSheet } from "./plotting/PlottingSheet.js";
import { ClassroomView } from "./classroom/ClassroomView.js";
import { AssessmentDashboard } from "./classroom/AssessmentDashboard.js";
import { Debrief } from "./classroom/Debrief.js";
import { Login } from "./classroom/Login.js";
import { SessionLobby } from "./classroom/SessionLobby.js";
import { DEFAULT_API, fetchMe, fetchRecording } from "./classroom/api.js";
import {
  FREE_DEMO_SCENARIO_ID,
  hasFullAccess,
  type EntitlementSnapshot,
} from "./access.js";
import { isPurchaseSupported, purchaseFullAccess } from "./billing.js";
import { extractTokenFromUrl, storeToken } from "./classroom/authToken.js";
import type { Recording } from "@radar-sim/engine";
import { Home } from "./marketing/Home.js";
import { DocPage, type DocKey } from "./marketing/DocPage.js";
import { HelmPanel } from "./helm/HelmPanel.js";
import { TargetReadout } from "./helm/TargetReadout.js";
import { SartRecoveryPanel } from "./helm/SartRecoveryPanel.js";
import {
  AddVesselPicker,
  type PendingSpawn,
} from "./helm/AddVesselPicker.js";
import { RecordingControls } from "./helm/RecordingControls.js";
import { SoloDebrief } from "./helm/SoloDebrief.js";
import { MarpaTracker } from "./ppi/marpa.js";
import { ChartView } from "./chart/ChartView.js";
import {
  CourseRecorderPanel,
  useCourseRecorder,
} from "./chart/CourseRecorder.js";
import { ErrorBoundary } from "./ui/ErrorBoundary.js";
import { Diagnostics } from "./diagnostics/Diagnostics.js";

type ChromeKey = "garmin" | "raymarine";
type View =
  | "home"
  | "simulator"
  | "authoring"
  | "plotting"
  | "classroom"
  | "diagnostics"
  | { doc: DocKey };

const DOC_HASHES: Record<string, DocKey> = {
  "#instructor-guide": "instructor-guide",
  "#syllabus-mapping": "syllabus-mapping",
  "#scenario-rationale": "scenario-rationale",
  "#privacy": "privacy",
  "#terms": "terms",
  "#store-links": "store-links",
};

function loadSavedScenarios(): ReadonlyArray<Scenario> {
  if (typeof window === "undefined") return [];
  const out: Scenario[] = [];
  for (const entry of listSavedScenarios()) {
    try {
      const parsed = ScenarioSchema.safeParse(JSON.parse(entry.json));
      if (parsed.success) out.push(parsed.data);
    } catch {
      // Skip unparseable entries; the authoring page can clean them up.
    }
  }
  return out;
}

function viewFromHash(): View | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  if (hash === "#diagnostics") return "diagnostics";
  const docKey = DOC_HASHES[hash];
  if (docKey !== undefined) return { doc: docKey };
  return null;
}

type ClassroomMode =
  | { kind: "session"; sessionId: string; role: "instructor" | "student"; scenarioId: string }
  | {
      kind: "assessment";
      assessmentId: string;
      scenarioId: string;
      role: "instructor" | "student";
      instanceSessionId?: string;
    }
  | { kind: "debrief"; recording: Recording };

interface ClassroomState {
  email: string | null;
  mode: ClassroomMode | null;
}

const CHROMES: Record<ChromeKey, { name: string; impl: ChromeComponent }> = {
  garmin: { name: "Garmin GPSMAP", impl: GarminGPSMAP },
  raymarine: { name: "Raymarine Axiom", impl: RaymarineAxiom },
};

/**
 * Phase-1 web shell. Loads a bundled scenario, runs `LocalSimSource`,
 * renders the PPI inside the Garmin chrome.
 */
export function App(): JSX.Element {
  const bundled = useMemo(() => listBundledScenarios(), []);
  const [view, setView] = useState<View>(() => viewFromHash() ?? "home");

  // React to hash navigation — covers the ErrorBoundary's diagnostics
  // link, the docs hash routes (#instructor-guide etc.), and anyone
  // bookmarking a deep URL.
  useEffect(() => {
    const onHash = () => {
      const v = viewFromHash();
      if (v !== null) setView(v);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  const [authoredScenario, setAuthoredScenario] = useState<Scenario | null>(null);
  const [savedScenarios, setSavedScenarios] = useState<ReadonlyArray<Scenario>>(
    () => loadSavedScenarios(),
  );
  const refreshSavedScenarios = () => setSavedScenarios(loadSavedScenarios());
  const [entitlement, setEntitlement] = useState<EntitlementSnapshot>({
    authenticated: false,
    skus: [],
  });
  const unlocked = hasFullAccess(entitlement);
  // Bundled scenarios are gated by entitlement: without the paid
  // `goastern.radar.sim.full` SKU only the free demo is exposed.
  // Authored / saved scenarios are user-created on this device and
  // stay available regardless — the gate is on the bundled library,
  // not the simulator engine itself.
  const scenarios = useMemo<ReadonlyArray<Scenario>>(() => {
    const head = authoredScenario === null ? [] : [authoredScenario];
    const allowedBundled = unlocked
      ? bundled
      : bundled.filter((s) => s.metadata.id === FREE_DEMO_SCENARIO_ID);
    return [...head, ...savedScenarios, ...allowedBundled];
  }, [bundled, authoredScenario, savedScenarios, unlocked]);
  const [scenarioId, setScenarioId] = useState<string>(
    bundled.find((s) => s.metadata.id === FREE_DEMO_SCENARIO_ID)?.metadata.id ??
      bundled[0]?.metadata.id ??
      "",
  );
  // Fall back to the demo scenario if the currently-selected one is
  // no longer in `scenarios` (e.g. the user signed out and the locked
  // scenarios disappeared, or a saved scenario was deleted).
  useEffect(() => {
    if (scenarios.find((s) => s.metadata.id === scenarioId)) return;
    const fallback = scenarios[0]?.metadata.id;
    if (fallback !== undefined && fallback !== scenarioId) {
      setScenarioId(fallback);
    }
  }, [scenarios, scenarioId]);
  const [chromeKey, setChromeKey] = useState<ChromeKey>("garmin");
  const scenario =
    scenarios.find((s) => s.metadata.id === scenarioId) ?? scenarios[0];

  const handleRunAuthored = (s: Scenario) => {
    setAuthoredScenario(s);
    setScenarioId(s.metadata.id);
    setView("simulator");
  };

  const [classroom, setClassroom] = useState<ClassroomState>({
    email: null,
    mode: null,
  });

  // Pull the user's auth state + entitlements on first mount and any
  // time the deep-link sign-in handoff stores a new token (the
  // listener below replays this same effect). Drives both the
  // classroom-mode email banner and the bundled-scenario gate.
  const refreshEntitlement = () => {
    void fetchMe(DEFAULT_API).then((me) => {
      setEntitlement({
        authenticated: me.authenticated,
        skus: me.skus,
      });
      if (me.authenticated && me.email !== undefined) {
        setClassroom((c) =>
          c.email === me.email ? c : { ...c, email: me.email ?? null },
        );
      } else if (!me.authenticated && classroom.email !== null) {
        setClassroom((c) => ({ ...c, email: null }));
      }
    });
  };

  useEffect(() => {
    refreshEntitlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capacitor sign-in handoff. The auth worker can't set a
  // `Domain=.goastern.com` cookie that the WebView at https://localhost
  // can read, so we stash the JWT from the deep-link URL fragment in
  // localStorage; `authHeaders()` in `api.ts` injects it as
  // `Authorization: Bearer …` on every fetch. After storing we kick
  // `fetchMe` immediately so the UI updates without waiting for the
  // user to navigate to the classroom view.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let handle: { remove: () => Promise<void> } | null = null;
    void CapacitorApp.addListener("appUrlOpen", (event: { url: string }) => {
      const token = extractTokenFromUrl(event.url);
      if (!token) return;
      storeToken(token);
      refreshEntitlement();
    }).then((h) => {
      handle = h;
    });
    return () => {
      void handle?.remove();
    };
  }, []);

  const openDebriefForSession = async (sessionId: string) => {
    const recording = await fetchRecording(DEFAULT_API, sessionId);
    setClassroom((c) => ({ ...c, mode: { kind: "debrief", recording } }));
  };

  const isDoc = typeof view === "object" && "doc" in view;
  const isSimulator = view === "simulator";
  const isHome = view === "home";

  return (
    <ErrorBoundary>
      <Header
        view={view}
        onPickView={setView}
        scenarios={scenarios}
        currentId={scenarioId}
        onPickScenario={setScenarioId}
        chromeKey={chromeKey}
        onPickChrome={setChromeKey}
        showSimulatorControls={isSimulator}
        authenticated={entitlement.authenticated}
        unlocked={unlocked}
        onBuy={() => {
          void purchaseFullAccess(() => refreshEntitlement());
        }}
      />
      <main
        style={
          isSimulator
            ? {
                // Viewport-bounded layout — never scrolls. Header is 56px
                // plus a 1px border, so reserve 57.
                height: "calc(100vh - 57px)",
                width: "100%",
                padding: "12px 16px",
                overflow: "hidden",
                boxSizing: "border-box",
              }
            : {
                minHeight: "calc(100vh - 57px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: isHome || isDoc ? 0 : "24px 16px",
                gap: 16,
              }
        }
      >
      {isHome && (
        <Home
          scenarios={scenarios}
          onLaunchDemo={(id) => {
            setScenarioId(id);
            setView("simulator");
          }}
          onEnterApp={() => setView("simulator")}
        />
      )}
      {isSimulator && scenario !== undefined && (
        <SessionRunner
          scenario={scenario}
          chromeKey={chromeKey}
          onScenarioSaved={(id) => {
            refreshSavedScenarios();
            setScenarioId(id);
          }}
        />
      )}
      {view === "authoring" && (
        <ScenarioAuthoring onRunScenario={handleRunAuthored} />
      )}
      {view === "plotting" && <PlottingSheet />}
      {view === "diagnostics" && <Diagnostics />}
      {view === "classroom" && (
        <ClassroomFlow
          state={classroom}
          onAuthenticated={(email) =>
            setClassroom((c) => ({ ...c, email }))
          }
          onSession={(session) =>
            setClassroom((c) => ({
              ...c,
              mode: { kind: "session", ...session },
            }))
          }
          onAssessment={(info) =>
            setClassroom((c) => ({ ...c, mode: { kind: "assessment", ...info } }))
          }
          onDebrief={openDebriefForSession}
          onLeave={() => setClassroom((c) => ({ ...c, mode: null }))}
          scenarios={scenarios}
        />
      )}
      {isDoc && typeof view === "object" && "doc" in view && (
        <DocPage docKey={view.doc} />
      )}
      </main>
      {!isSimulator && <GlobalFooter />}
    </ErrorBoundary>
  );
}

function ClassroomFlow(props: {
  state: ClassroomState;
  onAuthenticated: (email: string) => void;
  onSession: (s: {
    sessionId: string;
    role: "instructor" | "student";
    scenarioId: string;
  }) => void;
  onAssessment: (info: {
    assessmentId: string;
    scenarioId: string;
    role: "instructor" | "student";
    instanceSessionId?: string;
  }) => void;
  onDebrief: (sessionId: string) => Promise<void>;
  onLeave: () => void;
  scenarios: ReadonlyArray<Scenario>;
}): JSX.Element {
  if (props.state.email === null) {
    return <Login onAuthenticated={props.onAuthenticated} />;
  }
  if (props.state.mode === null) {
    return (
      <SessionLobby
        email={props.state.email}
        scenarios={props.scenarios}
        onSession={props.onSession}
        onAssessment={props.onAssessment}
      />
    );
  }
  if (props.state.mode.kind === "session") {
    return (
      <ClassroomViewWithDebrief
        email={props.state.email}
        sessionId={props.state.mode.sessionId}
        scenarioId={props.state.mode.scenarioId}
        role={props.state.mode.role}
        scenarios={props.scenarios}
        onLeave={props.onLeave}
        onDebrief={() => props.onDebrief(props.state.mode!.kind === "session" ? props.state.mode!.sessionId : "")}
      />
    );
  }
  if (props.state.mode.kind === "assessment") {
    if (props.state.mode.role === "student") {
      const studentSessionId = props.state.mode.instanceSessionId;
      if (studentSessionId === undefined) {
        return <p>Missing instance session id.</p>;
      }
      return (
        <ClassroomViewWithDebrief
          email={props.state.email}
          sessionId={studentSessionId}
          scenarioId={props.state.mode.scenarioId}
          role="student"
          scenarios={props.scenarios}
          onLeave={props.onLeave}
          onDebrief={() => props.onDebrief(studentSessionId)}
        />
      );
    }
    return (
      <AssessmentDashboard
        instructorEmail={props.state.email}
        assessmentId={props.state.mode.assessmentId}
        scenarios={props.scenarios}
        onLeave={props.onLeave}
      />
    );
  }
  return (
    <Debrief
      recording={props.state.mode.recording}
      scenarios={props.scenarios}
      onLeave={props.onLeave}
    />
  );
}

function ClassroomViewWithDebrief(props: {
  email: string;
  sessionId: string;
  scenarioId: string;
  role: "instructor" | "student";
  scenarios: ReadonlyArray<Scenario>;
  onLeave: () => void;
  onDebrief: () => Promise<void>;
}): JSX.Element {
  const [debriefing, setDebriefing] = useState(false);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <ClassroomView
        email={props.email}
        sessionId={props.sessionId}
        scenarioId={props.scenarioId}
        role={props.role}
        scenarios={props.scenarios}
        onLeave={props.onLeave}
      />
      <div style={{ alignSelf: "flex-end" }}>
        <button
          onClick={async () => {
            setDebriefing(true);
            try {
              await props.onDebrief();
            } finally {
              setDebriefing(false);
            }
          }}
          disabled={debriefing}
          style={{
            background: "#2f5c3f",
            color: "#cbe8d0",
            border: "1px solid #2a4a35",
            borderRadius: 4,
            padding: "6px 10px",
            fontFamily: "ui-monospace, monospace",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {debriefing ? "Loading…" : "Open debrief from this session"}
        </button>
      </div>
    </div>
  );
}

function Header(props: {
  view: View;
  onPickView: (v: View) => void;
  scenarios: ReadonlyArray<Scenario>;
  currentId: string;
  onPickScenario: (id: string) => void;
  chromeKey: ChromeKey;
  onPickChrome: (key: ChromeKey) => void;
  showSimulatorControls: boolean;
  authenticated: boolean;
  unlocked: boolean;
  onBuy: () => void;
}): JSX.Element {
  // Buy CTA only makes sense on iOS / Android — Apple and Google take
  // the payment, the web build can only sign in and unlock what was
  // already bought.
  const canPurchase = isPurchaseSupported();
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        background: "rgba(11, 18, 32, 0.85)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        borderBottom: "1px solid var(--border)",
        height: 56,
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "0 20px",
      }}
    >
      <button
        onClick={() => props.onPickView("home")}
        aria-label="Radar Sim home"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          background: "transparent",
          border: "none",
          color: "var(--text)",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <Logo />
        <span style={{ fontWeight: 600, fontSize: 15 }}>Radar Sim</span>
      </button>
      <a
        href="https://goastern.com/"
        title="GoAstern — passage planning"
        style={{
          fontSize: 12,
          color: "var(--text-dim)",
          textDecoration: "none",
          marginLeft: -4,
        }}
      >
        by <strong style={{ color: "var(--text-muted)" }}>GoAstern</strong> ↗
      </a>
      <ViewToggle view={props.view} onPick={props.onPickView} />
      <div style={{ flex: 1 }} />
      {props.showSimulatorControls && (
        <>
          <select
            value={props.currentId}
            onChange={(e) => props.onPickScenario(e.target.value)}
            aria-label="Pick a scenario"
            style={pickerStyle}
          >
            {props.scenarios.map((s) => (
              <option key={s.metadata.id} value={s.metadata.id}>
                {s.metadata.name}
              </option>
            ))}
          </select>
          <select
            value={props.chromeKey}
            onChange={(e) => props.onPickChrome(e.target.value as ChromeKey)}
            aria-label="Pick a device chrome"
            style={pickerStyle}
          >
            {(Object.entries(CHROMES) as [ChromeKey, (typeof CHROMES)[ChromeKey]][])
              .map(([key, c]) => (
                <option key={key} value={key}>
                  {c.name}
                </option>
              ))}
          </select>
          {!props.unlocked && (
            <span
              title={
                canPurchase && props.authenticated
                  ? "Buy the full library via the App Store / Play Store."
                  : props.authenticated
                    ? "Buy the iPad or Android tablet app to unlock the full library."
                    : "Sign in with your goastern.com account to access the full library."
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "rgba(255, 200, 80, 0.12)",
                color: "var(--text)",
                border: "1px solid rgba(255, 200, 80, 0.4)",
                borderRadius: 4,
                padding: "4px 10px",
                fontSize: 12,
              }}
            >
              <span aria-hidden="true">🔒</span>
              <span>Demo</span>
              <button
                onClick={() => {
                  if (canPurchase && props.authenticated) {
                    props.onBuy();
                  } else {
                    props.onPickView("classroom");
                  }
                }}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--accent)",
                  cursor: "pointer",
                  fontSize: 12,
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                {canPurchase && props.authenticated
                  ? "Buy"
                  : props.authenticated
                    ? "Get the app"
                    : "Sign in"}
              </button>
            </span>
          )}
        </>
      )}
    </header>
  );
}

/**
 * Persistent footer shown on every non-simulator view. Carries the
 * cross-link to the parent goastern.com app and the doc index — these
 * are the same destinations search-engines see at /*.md, but routed
 * through the in-app DocPage so the page renders in the site theme.
 */
function GlobalFooter(): JSX.Element {
  const docLinks: { hash: string; label: string }[] = [
    { hash: "#instructor-guide", label: "Instructor guide" },
    { hash: "#syllabus-mapping", label: "Syllabus mapping" },
    { hash: "#scenario-rationale", label: "Scenario rationale" },
    { hash: "#privacy", label: "Privacy" },
    { hash: "#terms", label: "Terms" },
  ];
  return (
    <footer
      style={{
        marginTop: "auto",
        borderTop: "1px solid var(--border)",
        background: "rgba(11, 18, 32, 0.6)",
        padding: "20px 24px",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        gap: 24,
        flexWrap: "wrap",
        alignItems: "center",
        fontSize: 13,
        color: "var(--text-muted)",
      }}
    >
      <a
        href="https://goastern.com/"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          color: "var(--text)",
          textDecoration: "none",
          fontWeight: 600,
        }}
      >
        <span style={{ color: "var(--accent)" }}>GoAstern</span>
        <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
          — passage planning ↗
        </span>
      </a>
      <nav
        aria-label="Documentation"
        style={{ display: "flex", gap: 14, flexWrap: "wrap" }}
      >
        {docLinks.map((d) => (
          <a key={d.hash} href={d.hash}>
            {d.label}
          </a>
        ))}
      </nav>
      <span style={{ marginLeft: "auto", color: "var(--text-dim)" }}>
        radar-simulator.goastern.com
      </span>
    </footer>
  );
}

function Logo(): JSX.Element {
  // Brand mark — warm amber radar PPI matching the GoAstern house
  // style. Hardcoded amber so the logo always reads as the brand
  // regardless of the surrounding UI accent.
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 32 32"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <defs>
        <linearGradient id="logo-sweep" x1="50%" y1="50%" x2="100%" y2="22%">
          <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="14.5" fill="#0b1220" />
      <circle
        cx="16"
        cy="16"
        r="14"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="1.4"
      />
      <circle
        cx="16"
        cy="16"
        r="9"
        fill="none"
        stroke="#f59e0b"
        strokeOpacity="0.35"
        strokeWidth="0.8"
      />
      <path
        d="M 16 16 L 30 9 A 14 14 0 0 0 27 4 Z"
        fill="url(#logo-sweep)"
      />
      <circle cx="23" cy="10" r="1.7" fill="#fbbf24" />
      <circle cx="16" cy="16" r="1.4" fill="#fef3c7" />
    </svg>
  );
}

function ViewToggle(props: {
  view: View;
  onPick: (v: View) => void;
}): JSX.Element {
  // Only the string variants of View show up in the toggle — the doc
  // routes are reached via the footer, not the top-nav.
  type ToggleKey = Extract<View, string>;
  const views: { key: ToggleKey; label: string }[] = [
    { key: "home", label: "Home" },
    { key: "simulator", label: "Simulator" },
    { key: "authoring", label: "Authoring" },
    { key: "plotting", label: "Plotting" },
    { key: "classroom", label: "Classroom" },
  ];
  return (
    <nav
      aria-label="Application views"
      style={{ display: "flex", gap: 2 }}
    >
      {views.map((v) => {
        const active = props.view === v.key;
        return (
          <button
            key={v.key}
            onClick={() => props.onPick(v.key)}
            aria-current={active ? "page" : undefined}
            aria-label={`Switch to ${v.label} view`}
            style={{
              background: active ? "var(--bg-card)" : "transparent",
              color: active ? "var(--text)" : "var(--text-muted)",
              border: "1px solid",
              borderColor: active ? "var(--border-strong)" : "transparent",
              borderRadius: "var(--radius-sm)",
              padding: "6px 12px",
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              transition: "color 120ms, background 120ms",
            }}
          >
            {v.label}
          </button>
        );
      })}
    </nav>
  );
}

const pickerStyle: React.CSSProperties = {
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  padding: "6px 10px",
  borderRadius: "var(--radius-sm)",
  fontSize: 13,
  cursor: "pointer",
};

function SessionRunner(props: {
  scenario: Scenario;
  chromeKey: ChromeKey;
  onScenarioSaved?: (id: string) => void;
}): JSX.Element {
  const { scenario, chromeKey } = props;
  const [display, setDisplay] = useState<DisplayState>(DEFAULT_DISPLAY_STATE);
  const [paused, setPaused] = useState(false);
  const [chartVisible, setChartVisible] = useState(false);
  const [pendingSpawn, setPendingSpawn] = useState<PendingSpawn | null>(null);
  const courseRecorder = useCourseRecorder();
  const [savedRecording, setSavedRecording] = useState<Recording | null>(null);
  const [debriefMode, setDebriefMode] = useState(false);

  const source = useMemo<RadarStateSource>(() => {
    const area = getAreaData(scenario.metadata.area);
    return new LocalSimSource(scenario, {
      areaData: asAreaDataLookup(area),
      isOnLand: makeIsOnLand(area),
      tidalAnomalies: getTidalAnomalies(scenario.metadata.area),
      tickRateHz: 10,
      autoTick: true,
    });
  }, [scenario]);
  // One MARPA tracker per scenario instance; shared between the PPI
  // (which calls update/acquire/drop) and the AIS readout panel
  // (which subscribes to track-list changes).
  const marpa = useMemo(() => new MarpaTracker(), [scenario.metadata.id]);

  useEffect(() => {
    void source.connect();
    return () => {
      void source.disconnect();
    };
  }, [source]);

  const area = getAreaData(scenario.metadata.area);
  const Chrome = CHROMES[chromeKey].impl;

  const togglePause = async () => {
    const next = !paused;
    setPaused(next);
    await source.sendCommand({ kind: next ? "pause" : "resume" });
  };

  // Layout — depends on which panels are showing. Possible states:
  //   - both radar and chart off → empty placeholder asking the user
  //     to enable one or the other
  //   - radar only         → 2 col: radar | helm
  //   - chart only         → 2 col: chart | helm
  //   - radar + chart      → 3 col: radar | chart | helm
  const radarOff = !display.radarVisible;
  const showChart = chartVisible;
  const bothOff = radarOff && !showChart;
  let gridCols: string;
  if (bothOff) {
    gridCols = "minmax(0, 2fr) minmax(320px, 1fr)";
  } else if (radarOff && showChart) {
    // Chart fills the simulator panel, helm on the right.
    gridCols = "minmax(0, 2fr) minmax(320px, 1fr)";
  } else if (showChart) {
    // Three-column: radar | chart | helm.
    gridCols = "minmax(0, 1.3fr) minmax(0, 1fr) minmax(320px, 1fr)";
  } else {
    // Two-column: radar | helm.
    gridCols = "minmax(0, 2fr) minmax(280px, 1fr)";
  }

  // Debrief mode swaps the entire simulator surface for a replay-
  // driven view. The live source keeps running underneath but its
  // UI is detached; coming out of debrief restores the live view.
  if (debriefMode && savedRecording !== null) {
    return (
      <SoloDebrief
        recording={savedRecording}
        area={area}
        onExit={() => setDebriefMode(false)}
      />
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: gridCols,
        // Single row that takes all available height. Without an
        // explicit row track, grid auto-sizes to content and the
        // children's intrinsic height escapes any overflow:auto on
        // the helm column — meaning the helm can't scroll. Forcing
        // `minmax(0, 1fr)` clamps the row to the parent's height.
        gridTemplateRows: "minmax(0, 1fr)",
        gap: 16,
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
      }}
    >
      {bothOff && (
        <NoPanelsPlaceholder
          onEnableRadar={() =>
            setDisplay((d) => ({ ...d, radarVisible: true }))
          }
          onEnableChart={() => setChartVisible(true)}
        />
      )}
      {!radarOff && (
        <div
          style={{
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Chrome
            ppi={
              <PPI
                source={source}
                area={area}
                scenarioId={scenario.metadata.id}
                display={display}
                onDisplayChange={setDisplay}
                marpa={marpa}
                {...(pendingSpawn !== null
                  ? { onPendingSpawnAt: (at: LatLon) => pendingSpawn.fulfil(at) }
                  : {})}
              />
            }
            display={display}
            onDisplayChange={setDisplay}
            scenarioName={scenario.metadata.name}
            paused={paused}
            onPause={togglePause}
            onResume={togglePause}
          />
        </div>
      )}
      {showChart && (
        <div style={{ minWidth: 0, minHeight: 0, display: "flex" }}>
          <ChartView
            source={source}
            area={area}
            display={display}
            onDisplayChange={setDisplay}
            marpa={marpa}
            {...(pendingSpawn !== null
              ? { onPendingSpawnAt: (at: LatLon) => pendingSpawn.fulfil(at) }
              : courseRecorder.recording
                ? { onPendingSpawnAt: (at: LatLon) => courseRecorder.add(at) }
                : {})}
          />
        </div>
      )}
      <div
        style={{
          minWidth: 0,
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          // iOS Safari / Capacitor needs this for momentum touch
          // scrolling inside a fixed-height container.
          WebkitOverflowScrolling: "touch",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          paddingBottom: 8,
        }}
      >
        {/* Targets first — when MARPA is tracking, the student needs
            the bearing/range/CPA at a glance. The helm controls below
            are useful too but never as urgent as collision-avoidance
            info. Real MFDs do the same: target box pops top-right.
            The MOB recovery panel sits between targets and the helm
            so it's prominent when active without pushing the helm's
            pause / sim-speed controls off the bottom. */}
        <TargetReadout source={source} marpa={marpa} />
        <SartRecoveryPanel
          source={source}
          onSetCourse={(deg) =>
            void source.sendCommand({ kind: "set_own_cog", cog: deg })
          }
        />
        <PanelToggles
          chartVisible={chartVisible}
          radarVisible={display.radarVisible}
          onChartToggle={() => setChartVisible((v) => !v)}
          onRadarToggle={() =>
            setDisplay((d) => ({ ...d, radarVisible: !d.radarVisible }))
          }
        />
        <AddVesselPicker
          source={source}
          onPendingSpawn={setPendingSpawn}
        />
        <CourseRecorderPanel
          api={courseRecorder}
          area={scenario.metadata.area}
          {...(props.onScenarioSaved !== undefined
            ? { onScenarioSaved: props.onScenarioSaved }
            : {})}
        />
        <RecordingControls
          source={source}
          scenarioId={scenario.metadata.id}
          scenarioName={scenario.metadata.name}
          savedRecording={savedRecording}
          onRecordingSaved={setSavedRecording}
          onOpenDebrief={() => setDebriefMode(true)}
          onClearRecording={() => setSavedRecording(null)}
          onOpenSavedRecording={(rec) => {
            setSavedRecording(rec);
            setDebriefMode(true);
          }}
        />
        <HelmPanel
          source={source}
          paused={paused}
          onTogglePause={togglePause}
          area={scenario.metadata.area}
        />
      </div>
    </div>
  );
}

/**
 * Shown in the simulator panel when both Radar and Chart are off.
 * Rather than leave the user staring at empty space, prompt them to
 * enable one or the other directly from the simulator surface.
 */
function NoPanelsPlaceholder(props: {
  onEnableRadar: () => void;
  onEnableChart: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: 32,
        textAlign: "center",
        gap: 16,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          fontSize: 32,
          opacity: 0.6,
          fontFamily: "var(--font-mono)",
        }}
      >
        ⚓
      </div>
      <Heading level={2} style={{ fontSize: 18 }}>
        Both Radar and Chart are off
      </Heading>
      <p
        style={{
          color: "var(--text-muted)",
          margin: 0,
          maxWidth: 360,
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        Enable at least one panel to start training. The radar shows
        what your antenna sees right now; the chart shows your position
        on the chart-table reference.
      </p>
      <Stack direction="row" gap={10}>
        <Button onClick={props.onEnableRadar}>Enable Radar</Button>
        <Button variant="secondary" onClick={props.onEnableChart}>
          Enable Chart
        </Button>
      </Stack>
    </div>
  );
}

/**
 * Unified panel-visibility toggle group. Both Radar and Chart panel
 * are layout-level toggles, so they live together in the helm column
 * rather than half-here-half-there.
 */
function PanelToggles(props: {
  chartVisible: boolean;
  radarVisible: boolean;
  onChartToggle: () => void;
  onRadarToggle: () => void;
}): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Panel visibility"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 6,
      }}
    >
      <PanelToggleButton
        label="Radar"
        enabled={props.radarVisible}
        onToggle={props.onRadarToggle}
      />
      <PanelToggleButton
        label="Chart"
        enabled={props.chartVisible}
        onToggle={props.onChartToggle}
      />
    </div>
  );
}

function PanelToggleButton(props: {
  label: string;
  enabled: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <button
      onClick={props.onToggle}
      aria-pressed={props.enabled}
      style={{
        background: props.enabled ? "var(--accent)" : "var(--bg-card)",
        color: props.enabled ? "var(--accent-fg)" : "var(--text)",
        border: `1px solid ${
          props.enabled ? "var(--accent)" : "var(--border-strong)"
        }`,
        borderRadius: "var(--radius-sm)",
        padding: "8px 10px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
      }}
    >
      <span>{props.label}</span>
      <span style={{ fontSize: 10, opacity: 0.8 }}>
        {props.enabled ? "ON" : "OFF"}
      </span>
    </button>
  );
}
